import { format as dateFnsFormat } from 'date-fns'

/**
 * Parse a date string as local time to avoid timezone issues
 * @param {string} dateString - Date string in any format
 * @returns {Date} - Date object in local time
 */
export const parseLocalDate = dateString => {
  if (!dateString) return null

  // If the date string is in yyyy-MM-dd format, add time component to force local time
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(dateString + 'T00:00:00')
  }

  // If it already has a time component or is ISO format, use as is
  return new Date(dateString)
}

/**
 * Format a date string for display, handling timezone issues
 * @param {string} dateString - Date string to format
 * @param {string} formatString - Format string for date-fns
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString, formatString = 'MMM dd, yyyy') => {
  if (!dateString) return '-'

  const date = parseLocalDate(dateString)
  if (isNaN(date.getTime())) return '-'

  return dateFnsFormat(date, formatString)
}

/**
 * Get current date in yyyy-MM-dd format for API calls
 * @returns {string} - Current date string
 */
export const getCurrentDateString = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format a date object to yyyy-MM-dd string for API calls
 * @param {Date} date - Date object
 * @returns {string} - Date string in yyyy-MM-dd format
 */
export const toDateString = date => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Normalize a date coming from either a date picker (Date) or the API (string)
 * into the yyyy-MM-dd string the API expects.
 * @param {Date|string} value - Date object or date string
 * @returns {string} - Date string in yyyy-MM-dd format, or '' when not a real date
 */
export const toApiDateString = value => {
  if (!value) return ''

  const date = value instanceof Date ? value : parseLocalDate(value)
  if (!date || isNaN(date.getTime())) return ''

  return toDateString(date)
}
